import {
	extractFieldsByFieldType,
	RUNNIT_NODE_FIELD_TYPE,
	RUNNIT_TYPE,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	RunnitNodeRun,
	RunnitNodeDef,
	RUNNIT_NODE_DEF_TOOL_TYPE,
	FieldDefUuid,
} from '@run-diffusion/shared';
import Vue from 'vue';
import { ROUTER } from '@/router/constants';
import { RunnitsCRUDMixin } from './RunnitsCRUDMixin';
import { TeamUserMixin } from './TeamUserMixin';
import { mapActions } from 'vuex';
import { analytics, EVENTS } from '@/services/analytics';

export const RunnitRemixMixin = Vue.extend({
	mixins: [
		RunnitsCRUDMixin,
		TeamUserMixin,
	],
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		async openToolWithSameInputs (nodeDef: RunnitNodeDef, nodeRun: RunnitNodeRun, nodeRunId: string, runnitId?: string, nodeId?: string) {
			analytics.track(EVENTS.REMIX_INITIATED, {
				node_def_id: (nodeDef && (nodeDef as any).id) || null,
				node_run_id: nodeRunId || null,
				source: runnitId ? 'board_remix' : 'carousel_remix',
			});
			const inputValues: Record<FieldDefUuid, any> = await this.fetchNodeRunValues(nodeRun, nodeRunId);
			const encodedInputValues: string = encodeURIComponent(JSON.stringify(inputValues));

			const onCloseCarousel: Function = async (): Promise<void> => {
				await new Promise(resolve => setTimeout(resolve, 250)); // wait for routing a little bit before closing
				this.$emit('close-carousel', true);
			};

			// Check for existing kit query parameter
			const existingKit = this.$route.query.kit;

			// If we're on a RunnitBoard, just update the query params
			if (runnitId && nodeId && this.$route.params.runnitId === runnitId) {
				// Set the selectedNodeId to match the nodeId from the generation
				// This needs to happen before the routerPush so that the selected node is set correctly
				this.updateRunnitState({
					selectedNodeId: nodeId
				});

				// Build query object with inputValues and kit if it exists
				const query: any = {
					inputValues: encodedInputValues
				};
				if (existingKit) {
					query.kit = existingKit;
				}

				// Update route and selectedNodeId
				await this.routerPush(this.$route, this.$router, {
					name: ROUTER.RUNNITS_BOARD,
					params: {
						runnitId: runnitId || this.$route.params.runnitId,
					},
					query,
				});

				await onCloseCarousel();
				return;
			}

			// Otherwise create a new single tool runnit
			const options: any = {
				inputValues: encodedInputValues
			};
			if (existingKit) {
				options.kit = existingKit;
			}

			this.onAddRunnitClick(nodeDef, RUNNIT_TYPE.SINGLE_TOOL, options);

			await onCloseCarousel();
		},
		async fetchNodeRunValues (nodeRun: RunnitNodeRun, nodeRunId: string) {
			try {
				const fieldDefUuidToTypeMap = this.mapFieldDefUuidToFieldType(nodeRun.fields);
				const inputs = { ...nodeRun.inputs }
				Object.keys(fieldDefUuidToTypeMap).forEach(inputUuid => {
					if (
						fieldDefUuidToTypeMap[inputUuid] === RUNNIT_NODE_FIELD_TYPE.PROMPT &&
						nodeRun.promptMagic?.results?.[inputUuid]
					) {
						inputs[inputUuid] = nodeRun.promptMagic.results[inputUuid];
					} else if (fieldDefUuidToTypeMap[inputUuid] === RUNNIT_NODE_FIELD_TYPE.SEED) {
						delete inputs[inputUuid];
					}
				});
				return inputs;
			} catch (err) {
				console.error('Failed to preload settings', err);
			}
		},
		mapFieldDefUuidToFieldType (fields: (RunnitNodeFieldGroup | RunnitNodeField)[]) {
			let map = {}
			for (const field of (fields || [])) {
				if ((field as RunnitNodeFieldGroup).__rgroup) {
					map = {
						...this.mapFieldDefUuidToFieldType((field as RunnitNodeFieldGroup).fields),
						...map
					};
				} else if ((field as RunnitNodeField).__rfield) {
					map[(field as RunnitNodeField).fieldDefUuid] = (field as RunnitNodeField).type;
				}
			}
			return map;
		},
		canRemix (nodeRun: RunnitNodeRun): boolean {
			if (!nodeRun || !nodeRun.nodeDef) return false;

			// Is the tool published?
			if (!nodeRun.nodeDef.isPublished) return false;

			// Is the tool blocked for this team user?
			if (this.isNodeDefBlockedForTeamUser(nodeRun.nodeDef)) return false;

			// Does the user have access to the company tool?
			if (nodeRun.nodeDef.type === RUNNIT_NODE_DEF_TOOL_TYPE.COMPANY) {
				if (!this.team?.companyId) return false;
				if (!nodeRun.nodeDef.companyIds?.[this.team.companyId]) return false;
			}

			// Does the user have access to the model used?
			const modelFields: RunnitNodeField[] = extractFieldsByFieldType(nodeRun.nodeDef.fields, RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT);
			if (!modelFields.length) return true;
			const model = nodeRun.inputs[modelFields[0].fieldDefUuid];

			if (model) {
				if (!this.user) return false;
				if (model.teamId && !this.user.teamIds[model.teamId]) return false;
				if (model.userId && model.userId !== this.user.id) return false;
			}

			return true;
		},
	},
});
